/**
 * Hook per la gestione della guida utente
 * 
 * Fornisce:
 * - Recupero categorie e articoli filtrati per ruolo utente
 * - Tooltip contestuali basati su related_feature
 * - Invio domande utente
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// ============================================================================
// TYPES
// ============================================================================

export interface GuideCategory {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  icon: string | null;
  order_index: number;
  applicable_roles: string[];
  is_published: boolean;
}

export interface GuideArticle {
  id: string;
  category_id: string;
  slug: string;
  title: string;
  summary: string | null;
  content: string | null;
  tooltip_text: string | null;
  applicable_roles: string[];
  related_feature: string | null;
  order_index: number;
  is_published: boolean;
}

export interface GuideArticleWithCategory extends GuideArticle {
  category: GuideCategory;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook per recuperare le categorie della guida
 */
export function useGuideCategories() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["guide-categories", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("guide_categories")
        .select("*")
        .eq("is_published", true)
        .order("order_index");

      if (error) throw error;
      return data as GuideCategory[];
    },
    enabled: !!user,
  });
}

/**
 * Hook per recuperare gli articoli di una categoria
 */
export function useGuideArticles(categorySlug?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["guide-articles", categorySlug, user?.id],
    queryFn: async () => {
      let query = supabase
        .from("guide_articles")
        .select(`
          *,
          category:guide_categories!category_id (*)
        `)
        .eq("is_published", true)
        .order("order_index");

      if (categorySlug) {
        // Prima otteniamo l'ID della categoria
        const { data: category } = await supabase
          .from("guide_categories")
          .select("id")
          .eq("slug", categorySlug)
          .single();

        if (category) {
          query = query.eq("category_id", category.id);
        }
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as GuideArticleWithCategory[];
    },
    enabled: !!user,
  });
}

/**
 * Hook per recuperare un singolo articolo
 */
export function useGuideArticle(categorySlug: string, articleSlug: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["guide-article", categorySlug, articleSlug, user?.id],
    queryFn: async () => {
      // Prima otteniamo l'ID della categoria
      const { data: category } = await supabase
        .from("guide_categories")
        .select("id")
        .eq("slug", categorySlug)
        .single();

      if (!category) throw new Error("Categoria non trovata");

      const { data, error } = await supabase
        .from("guide_articles")
        .select(`
          *,
          category:guide_categories!category_id (*)
        `)
        .eq("category_id", category.id)
        .eq("slug", articleSlug)
        .eq("is_published", true)
        .single();

      if (error) throw error;
      return data as GuideArticleWithCategory;
    },
    enabled: !!user && !!categorySlug && !!articleSlug,
  });
}

/**
 * Hook per recuperare il tooltip di una feature
 */
export function useFeatureTooltip(featureKey: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["guide-tooltip", featureKey, user?.id],
    queryFn: async () => {
      if (!featureKey) return null;

      const { data, error } = await supabase
        .from("guide_articles")
        .select(`
          id,
          title,
          tooltip_text,
          slug,
          category:guide_categories!category_id (slug)
        `)
        .eq("related_feature", featureKey)
        .eq("is_published", true)
        .single();

      if (error) {
        // Se non trova l'articolo, ritorna null (non è un errore)
        if (error.code === "PGRST116") return null;
        throw error;
      }
      return data;
    },
    enabled: !!user && !!featureKey,
  });
}

/**
 * Hook per inviare una domanda
 */
export function useSubmitQuestion() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      question,
      contextPage,
      contextFeature,
    }: {
      question: string;
      contextPage?: string;
      contextFeature?: string;
    }) => {
      if (!user) throw new Error("Utente non autenticato");

      const { data, error } = await supabase
        .from("user_questions")
        .insert({
          user_id: user.id,
          question,
          context_page: contextPage,
          context_feature: contextFeature,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-questions"] });
    },
  });
}

/**
 * Hook per recuperare le domande dell'utente
 */
export function useUserQuestions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-questions", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_questions")
        .select("*")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}
